import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import type {
  Sport,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import type {
  TokenPotentialDiscount,
  TokenPotentialDiscountVariables,
} from './__generated__/usePotentialDiscount.graphql.ts';

const TOKEN_POTENTIAL_DISCOUNT_QUERY = gql`
  query TokenPotentialDiscount(
    $amount: String!
    $currency: SupportedCurrency!
    $sport: Sport!
    $exchangeRateId: ID!
    $auctionId: ID
  ) {
    market {
      id
      potentialDiscount(
        amount: { amount: $amount, currency: $currency }
        exchangeRateId: $exchangeRateId
        auctionId: $auctionId
        sport: $sport
      )
    }
  }
` as TypedDocumentNode<TokenPotentialDiscount, TokenPotentialDiscountVariables>;

export const usePotentialDiscount = (
  monetaryAmount: MonetaryAmount,
  referenceCurrency: SupportedCurrency,
  {
    auctionId,
    sport: sportProp,
    canDiscount = true,
  }: {
    auctionId?: string;
    sport?: Sport;
    canDiscount?: boolean;
  } = {}
): MonetaryAmount | undefined => {
  const { exchangeRate } = useConfigContext();
  const { sport: sportContext } = useSportContext();

  const sport = sportProp || sportContext;

  const { data, loading } = useQuery(TOKEN_POTENTIAL_DISCOUNT_QUERY, {
    variables: {
      amount: monetaryAmount.readAmount(referenceCurrency).toString(),
      currency: referenceCurrency,
      exchangeRateId: idFromObject(exchangeRate.id),
      auctionId,
      sport,
    },
    skip:
      !monetaryAmount ||
      monetaryAmount.isZero ||
      !exchangeRate.id ||
      !canDiscount,
  });

  const potentialDiscount = useMemo(() => {
    const discount = data?.market.potentialDiscount;
    return discount
      ? MonetaryAmount.fromReferenceCurrency(
          referenceCurrency,
          discount,
          exchangeRate.ethRates
        )
      : MonetaryAmount.zero;
  }, [
    data?.market.potentialDiscount,
    referenceCurrency,
    exchangeRate.ethRates,
  ]);

  if (loading) {
    return undefined;
  }

  return potentialDiscount;
};
